import React, { useState } from 'react';
import { Search, Copy, CheckCircle, AlertCircle, Clock, Activity } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { adminLogsApi, LogSearchResponse, XRayTraceInfo } from '../../services/adminLogsApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LogsSearch: React.FC = () => {
  const { canManageCompanies } = usePermissions();
  const [traceId, setTraceId] = useState('');
  const [hoursBack, setHoursBack] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<LogSearchResponse | null>(null);
  const [traceInfo, setTraceInfo] = useState<XRayTraceInfo | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSearch = async () => {
    if (!traceId.trim()) {
      setError('Por favor ingresa un Trace ID');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);
    setTraceInfo(null);

    try {
      // Search logs
      const logsResult = await adminLogsApi.searchByTraceId(traceId.trim(), hoursBack);
      setSearchResult(logsResult);

      // Try to get X-Ray trace info (may fail if trace not found)
      try {
        const xrayResult = await adminLogsApi.getTraceInfo(traceId.trim());
        setTraceInfo(xrayResult);
      } catch (xrayError) {
        console.warn('X-Ray trace not found:', xrayError);
      }
    } catch (err: any) {
      setError(err.message || 'Error al buscar logs');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTraceId = () => {
    navigator.clipboard.writeText(traceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  if (!canManageCompanies) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-yellow-800 mb-2">Acceso Restringido</h2>
        <p className="text-yellow-700">
          Solo los superadministradores pueden buscar logs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Búsqueda de Logs</h1>
        <p className="mt-2 text-sm text-gray-600">
          Busca logs de CloudWatch usando el Trace ID para debugging
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="traceId" className="block text-sm font-medium text-gray-700 mb-2">
              Trace ID
            </label>
            <div className="flex gap-2">
              <input
                id="traceId"
                type="text"
                value={traceId}
                onChange={(e) => setTraceId(e.target.value)}
                placeholder="Root=1-xxx-xxx"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleCopyTraceId}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title="Copiar Trace ID"
              >
                {copied ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="hoursBack" className="block text-sm font-medium text-gray-700 mb-2">
              Buscar en las últimas
            </label>
            <select
              id="hoursBack"
              value={hoursBack}
              onChange={(e) => setHoursBack(Number(e.target.value))}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value={1}>1 hora</option>
              <option value={6}>6 horas</option>
              <option value={24}>24 horas</option>
              <option value={72}>3 días</option>
              <option value={168}>7 días</option>
            </select>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading || !traceId.trim()}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Buscando...
              </>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Buscar Logs
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* X-Ray Trace Info */}
      {traceInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-medium text-blue-900">Información del Trace</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {traceInfo.duration && (
              <div>
                <span className="text-blue-700 font-medium">Duración:</span>
                <span className="ml-2 text-blue-900">{traceInfo.duration.toFixed(2)} ms</span>
              </div>
            )}
            {traceInfo.statusCode && (
              <div>
                <span className="text-blue-700 font-medium">Status:</span>
                <span className={`ml-2 font-semibold ${
                  traceInfo.statusCode >= 200 && traceInfo.statusCode < 300
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {traceInfo.statusCode}
                </span>
              </div>
            )}
            {traceInfo.method && (
              <div>
                <span className="text-blue-700 font-medium">Método:</span>
                <span className="ml-2 text-blue-900">{traceInfo.method}</span>
              </div>
            )}
            {traceInfo.url && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-blue-700 font-medium">URL:</span>
                <span className="ml-2 text-blue-900 break-all">{traceInfo.url}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResult && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Logs Encontrados: {searchResult.totalLogs}
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{searchResult.searchDuration.toFixed(3)}s</span>
                </div>
              </div>
            </div>
          </div>

          {searchResult.totalLogs === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">
              No se encontraron logs para este Trace ID
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {searchResult.logs.map((log, index) => {
                // Try to parse structured JSON logs
                let parsed: any = null;
                try {
                  if (log.message.startsWith('{')) {
                    parsed = JSON.parse(log.message);
                  }
                } catch { /* not JSON */ }

                const levelColor = parsed?.level === 'ERROR'
                  ? 'border-l-red-500 bg-red-50/50'
                  : parsed?.level === 'WARN'
                    ? 'border-l-yellow-500 bg-yellow-50/50'
                    : parsed?.event === 'REQUEST_START'
                      ? 'border-l-blue-500 bg-blue-50/30'
                      : '';

                return (
                  <div key={index} className={`px-6 py-4 hover:bg-gray-50 ${parsed ? `border-l-4 ${levelColor}` : ''}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 text-xs text-gray-500 font-mono w-40">
                        {formatTimestamp(log.timestamp)}
                      </div>
                      <div className="flex-1 min-w-0">
                        {parsed ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                parsed.level === 'ERROR' ? 'bg-red-100 text-red-800' :
                                parsed.level === 'WARN' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {parsed.level}
                              </span>
                              <span className="text-xs font-semibold text-gray-900">{parsed.event}</span>
                              {parsed.method && (
                                <span className="text-xs text-gray-600">{parsed.method} {parsed.path}</span>
                              )}
                              {parsed.status && (
                                <span className={`text-xs font-semibold ${parsed.status >= 400 ? 'text-red-600' : 'text-green-600'}`}>
                                  {parsed.status}
                                </span>
                              )}
                              {parsed.durationMs && (
                                <span className="text-xs text-gray-500">{parsed.durationMs}ms</span>
                              )}
                            </div>
                            {parsed.user?.email && (
                              <div className="text-xs text-gray-600">
                                Usuario: {parsed.user.email} ({parsed.user.role}) — Empresa: {parsed.impersonateCompany || parsed.user.companyId}
                              </div>
                            )}
                            {parsed.detail && (
                              <div className="text-xs text-red-700 font-medium mt-1">{parsed.detail}</div>
                            )}
                            {parsed.error && (
                              <div className="text-xs text-red-700 font-medium mt-1">{parsed.errorType}: {parsed.error}</div>
                            )}
                            {parsed.validationErrors && (
                              <pre className="text-xs text-orange-700 mt-1 whitespace-pre-wrap">{JSON.stringify(parsed.validationErrors, null, 2)}</pre>
                            )}
                            {parsed.requestBody && (
                              <details className="mt-1">
                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Request Body</summary>
                                <pre className="text-xs text-gray-700 mt-1 whitespace-pre-wrap bg-gray-100 p-2 rounded">{JSON.stringify(parsed.requestBody, null, 2)}</pre>
                              </details>
                            )}
                            {parsed.traceback && (
                              <details className="mt-1">
                                <summary className="text-xs text-red-500 cursor-pointer hover:text-red-700">Traceback</summary>
                                <pre className="text-xs text-red-700 mt-1 whitespace-pre-wrap bg-red-50 p-2 rounded">{parsed.traceback}</pre>
                              </details>
                            )}
                          </div>
                        ) : (
                          <pre className="text-xs text-gray-900 whitespace-pre-wrap break-words font-mono">
                            {log.message}
                          </pre>
                        )}
                        <div className="mt-1 text-xs text-gray-400">
                          Stream: {log.logStreamName}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LogsSearch;
